/**
 * Agents Page - Display selected specialist agents
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { Agent } from '../types/session';

const AgentsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const response = await apiClient.get(`/api/sessions/${sessionId}/agents`);
        setAgents(response.data.agents || []);
        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load agents');
        setLoading(false);
      }
    };

    loadAgents();
  }, [sessionId]);

  const handleStartQA = () => {
    navigate(`/qa/${sessionId}`);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Loading agents...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
        }}>
          <h3>Error Loading Agents</h3>
          <p>{error}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <h2>No Agents Selected</h2>
        <p>No specialist agents matched your business context. Please try again with different objectives.</p>
        <button
          onClick={() => navigate('/')}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Start New Analysis
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '900px', margin: '0 auto' }}>
      <header style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '8px' }}>Selected Specialist Agents</h1>
        <p style={{ color: '#666' }}>
          Based on your business context, we've selected {agents.length} specialist agent{agents.length !== 1 ? 's' : ''} to provide strategic insights
        </p>
      </header>

      <div style={{ display: 'grid', gap: '24px', marginBottom: '40px' }}>
        {agents.map((agent, index) => (
          <div
            key={agent.agent_id}
            style={{
              padding: '24px',
              border: '2px solid #007bff',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '1.4rem', marginBottom: '8px', color: '#007bff' }}>
                  {index + 1}. {agent.name}
                </h3>
                <p style={{ color: '#666', marginBottom: '12px' }}>
                  {agent.description}
                </p>
              </div>
              <div
                style={{
                  padding: '8px 16px',
                  backgroundColor: agent.relevance_score >= 80 ? '#28a745' : agent.relevance_score >= 60 ? '#ffc107' : '#6c757d',
                  color: 'white',
                  borderRadius: '20px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  marginLeft: '16px',
                }}
              >
                {agent.relevance_score.toFixed(0)}% Match
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center' }}>
        <button
          onClick={handleStartQA}
          style={{
            padding: '16px 32px',
            fontSize: '1.1rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          Start Q&amp;A Sessions
        </button>
        <p style={{ marginTop: '16px', fontSize: '0.9rem', color: '#666' }}>
          Each specialist will ask you questions to better understand your needs
        </p>
      </div>
    </div>
  );
};

export default AgentsPage;
